import { ArrowLeft, Pencil, Printer, Trash2, X } from 'lucide-react';
import {
  type CSSProperties,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/cn';

export interface ModalContentWrapperProps {
  open: boolean;
  children: ReactNode;
  title?: string | ReactNode;
  headerAction?: React.ReactNode;
  variant?: 'center' | 'right';
  onClose: () => void;
  onBack?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  showClose?: boolean;
  modalScrollRef?: Ref<HTMLDivElement>;
  scrollableTarget?: string;
  classes?: {
    titleWrapper?: string;
    title?: string;
    dialog?: string;
    dialogPanel?: string;
    children?: string;
  };
  showPrint?: boolean;
  persistOnNavigate?: boolean;
  style?: CSSProperties;
  closeOnBackdropClick?: boolean;
}

export const ModalContentWrapper = ({
  open,
  title,
  headerAction,
  onBack,
  onClose,
  classes,
  children,
  onDelete,
  onEdit,
  modalScrollRef,
  scrollableTarget,
  variant = 'center',
  showClose = true,
  showPrint = false,
  persistOnNavigate = false,
  style,
  closeOnBackdropClick = true,
}: ModalContentWrapperProps) => {
  const location = useLocation();
  const currentPathName = useRef(location.pathname);
  const panelRef = useRef<HTMLDivElement>(null);
  const pointerDownInsidePanelRef = useRef(false);

  useEffect(() => {
    if (persistOnNavigate) return;
    if (location.pathname !== currentPathName.current) {
      onClose();
      currentPathName.current = location.pathname;
    }
  }, [location.pathname, onClose, persistOnNavigate]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, open]);

  const printHandler = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  }, []);

  if (!open) return null;

  return createPortal(
    <div className={cn('relative z-50', classes?.dialog)}>
      <div className="fixed inset-0 bg-black/25 pointer-events-auto" />
      <div
        className={cn('fixed inset-0 overflow-y-auto', {
          'right-0': variant === 'right',
        })}
        id={scrollableTarget || 'modal_scroll'}
        ref={modalScrollRef}
      >
        <div
          className={cn('flex min-h-full text-center', {
            'justify-center items-center p-4': variant === 'center',
            'justify-end': variant === 'right',
          })}
          onMouseDownCapture={event => {
            pointerDownInsidePanelRef.current = !!panelRef.current?.contains(event.target as Node);
          }}
          onClick={event => {
            const shouldClose =
              closeOnBackdropClick &&
              event.target === event.currentTarget &&
              !pointerDownInsidePanelRef.current;

            pointerDownInsidePanelRef.current = false;

            if (shouldClose) {
              onClose();
            }
          }}
        >
          <div
            className={cn(
              'w-full transform text-left align-middle transition-all break-words',
              {
                'rounded-xl max-w-xl bg-white shadow-xl': variant === 'center',
                'grid grid-rows-[auto_1fr] max-w-[475px] bg-white shadow-xl': variant === 'right',
              },
              classes?.dialogPanel,
            )}
            style={{ wordBreak: 'break-word', ...style }}
            ref={panelRef}
            onMouseDown={event => event.stopPropagation()}
            onMouseUp={event => event.stopPropagation()}
            onClick={event => event.stopPropagation()}
          >
            {(title ||
              headerAction ||
              onBack ||
              showClose === true ||
              onEdit ||
              onDelete ||
              showPrint) && (
              <div className="w-full flex items-start justify-between px-3 sm:px-6 pt-5 min-w-0">
                <div className={cn('flex gap-3 items-center min-w-0', classes?.titleWrapper)}>
                  {onBack && (
                    <button
                      onClick={onBack}
                      className="flex-none hover:opacity-80 hover:cursor-pointer"
                    >
                      <ArrowLeft className="w-6 h-6 min-w-6 min-h-6" />
                    </button>
                  )}
                  {title && (
                    <span
                      className={cn(
                        'text-[24px] leading-[32px] break-words min-w-0',
                        {
                          'font-medium font-serif': !classes?.title?.includes('font-'),
                        },
                        classes?.title,
                      )}
                      style={{ wordBreak: 'break-word' }}
                    >
                      {title}
                    </span>
                  )}
                </div>
                <div className="flex gap-3 items-center select-none">
                  {headerAction}
                  {showPrint && (
                    <span onClick={printHandler} className="hover:opacity-80 cursor-pointer">
                      <Printer className="w-6 h-6 min-w-6 min-h-6" />
                    </span>
                  )}
                  {onEdit && (
                    <span onClick={onEdit} className="hover:opacity-80 cursor-pointer">
                      <Pencil className="w-6 h-6 min-w-6 min-h-6" />
                    </span>
                  )}
                  {onDelete && (
                    <span onClick={onDelete} className="hover:opacity-80 cursor-pointer">
                      <Trash2 className="w-6 h-6 min-w-6 min-h-6" />
                    </span>
                  )}
                  {showClose === true && (
                    <span onClick={onClose} className="hover:opacity-80 cursor-pointer">
                      <X className="w-6 h-6 min-w-6 min-h-6" />
                    </span>
                  )}
                </div>
              </div>
            )}
            <div
              className={cn(
                {
                  'pb-5': !classes?.children?.includes('pb-'),
                  'p-3 sm:p-6': !classes?.children?.includes('p-'),
                },
                classes?.children,
              )}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
